import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  PhotoIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ScissorsIcon,
  PencilIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ImageCropper from './ImageCropper';

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DocumentViewerProps {
  documentId: number;
  fileName: string;
  filePath: string;
  onClose: () => void;
  onDocumentUpdated?: () => void;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  documentId,
  fileName,
  filePath,
  onClose,
  onDocumentUpdated,
}) => {
  useContext(AuthContext);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1.0);
  const [isMobile] = useState(window.innerWidth < 768);
  const [touchStart, setTouchStart] = useState<{ distance: number } | null>(null);
  const [initialScale, setInitialScale] = useState(1.0);
  const [showCropper, setShowCropper] = useState(false);
  const [cropLoading, setCropLoading] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(fileName);
  const [currentFileName, setCurrentFileName] = useState(fileName);

  const urlRef = useRef<string | null>(null);

  const isPDF = filePath.toLowerCase().endsWith('.pdf');
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(filePath);

  // Touch handlers for pinch-to-zoom
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      setTouchStart({ distance });
      setInitialScale(scale);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && touchStart) {
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const scaleChange = distance / touchStart.distance;
      const newScale = Math.max(0.5, Math.min(3, initialScale * scaleChange));
      setScale(newScale);
    }
  };

  const handleTouchEnd = () => {
    setTouchStart(null);
  };

  // Reset zoom
  const resetZoom = () => {
    setScale(1.0);
  };

  const loadDocument = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get(`/api/documents/download/${documentId}`, {
        responseType: 'blob',
        signal,
        headers: {
          'Accept': isPDF ? 'application/pdf' : 'image/*'
        }
      });

      if (signal?.aborted) return;

      // Alte URL freigeben
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
      }

      const blob = response.data;
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      setDocumentUrl(url);
    } catch (err: any) {
      if (signal?.aborted) return;
      console.error('Error loading document:', err);
      setError(`Dokument konnte nicht geladen werden: ${err.message || 'Unbekannter Fehler'}`);
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [documentId, isPDF]);

  useEffect(() => {
    const controller = new AbortController();
    loadDocument(controller.signal);
    return () => {
      controller.abort();
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [loadDocument]);

  const changeScale = (delta: number) => {
    setScale(prevScale => {
      const newScale = prevScale + delta;
      if (newScale < 0.5) return 0.5;
      if (newScale > 3) return 3;
      return newScale;
    });
  };

  const downloadDocument = () => {
    if (documentUrl) {
      const link = document.createElement('a');
      link.href = documentUrl;
      link.download = currentFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleSaveName = async () => {
    const newName = editedName.trim();
    if (!newName || newName === currentFileName) {
      setIsEditingName(false);
      setEditedName(currentFileName);
      return;
    }
    try {
      await apiClient.put(`/api/documents/${documentId}`, { name: newName });
      setCurrentFileName(newName);
      setIsEditingName(false);
      if (onDocumentUpdated) onDocumentUpdated();
    } catch (err) {
      console.error('Error updating document name:', err);
      setEditedName(currentFileName);
      setIsEditingName(false);
    }
  };

  const handleCrop = async (cropArea: CropArea) => {
    setCropLoading(true);
    try {
      const response = await apiClient.post(`/api/documents/crop/${documentId}`, {
        x: cropArea.x,
        y: cropArea.y,
        width: cropArea.width,
        height: cropArea.height,
      });

      if (response.data.success) {
        setShowCropper(false);
        // Lade das Bild neu
        await loadDocument();
        // Benachrichtige Parent-Komponente
        if (onDocumentUpdated) {
          onDocumentUpdated();
        }
      }
    } catch (err: any) {
      console.error('Crop-Fehler:', err);
      alert(err.response?.data?.error || 'Fehler beim Zuschneiden des Bildes');
    } finally {
      setCropLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex flex-col z-50">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-3 sm:px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 group">
            <div className="flex-shrink-0">
              {isPDF ? (
                <DocumentTextIcon className="h-5 w-5 sm:h-6 sm:w-6 text-error-500" />
              ) : (
                <PhotoIcon className="h-5 w-5 sm:h-6 sm:w-6 text-info-500" />
              )}
            </div>
            {isEditingName ? (
              <div className="flex items-center gap-1.5 min-w-0 flex-1">
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') {
                      setIsEditingName(false);
                      setEditedName(currentFileName);
                    }
                  }}
                  className="flex-1 min-w-0 px-2 py-1 text-sm sm:text-lg font-semibold bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  autoFocus
                />
                <button
                  onClick={handleSaveName}
                  className="p-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex-shrink-0"
                >
                  <CheckIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => {
                    setIsEditingName(false);
                    setEditedName(currentFileName);
                  }}
                  className="p-1.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition flex-shrink-0"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsEditingName(true)}
                className="flex items-center gap-1.5 min-w-0 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                title="Dateiname bearbeiten"
              >
                <h2 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {currentFileName}
                </h2>
                <PencilIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Zoom Controls for Images */}
            {isImage && documentUrl && (
              <>
                <button
                  onClick={() => changeScale(-0.1)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Verkleinern"
                >
                  <MagnifyingGlassMinusIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>
                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 min-w-[45px] sm:min-w-[60px] text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={() => changeScale(0.1)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Vergrößern"
                >
                  <MagnifyingGlassPlusIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>

                {/* Crop-Button */}
                <button
                  onClick={() => setShowCropper(true)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Bild zuschneiden"
                >
                  <ScissorsIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>
              </>
            )}

            {/* Reset Zoom for Images on Mobile */}
            {isImage && documentUrl && isMobile && scale !== 1 && (
              <button
                onClick={resetZoom}
                className="p-1.5 bg-info-100 dark:bg-info-900/30 hover:bg-info-200 dark:hover:bg-info-900/50 rounded-lg transition-colors"
                title="Zoom zurücksetzen"
              >
                <span className="text-xs font-medium text-info-700 dark:text-info-300">100%</span>
              </button>
            )}

            {/* Download - Hidden on Mobile for space */}
            {!isMobile && (
              <button
                onClick={downloadDocument}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Download"
              >
                <ArrowDownTrayIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            )}

            {/* Close - Always visible, bigger on mobile */}
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              title="Schließen"
            >
              <XMarkIcon className="h-6 w-6 sm:h-5 sm:w-5 text-gray-700 dark:text-gray-300" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div
        className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-900 p-2 sm:p-4"
        onTouchStart={isImage ? handleTouchStart : undefined}
        onTouchMove={isImage ? handleTouchMove : undefined}
        onTouchEnd={isImage ? handleTouchEnd : undefined}
      >
        {loading && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Lade Dokument...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <DocumentTextIcon className="h-12 w-12 text-error-500 mx-auto mb-4" />
              <p className="text-error-600 dark:text-error-400">{error}</p>
              <button
                onClick={() => loadDocument()}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Erneut versuchen
              </button>
            </div>
          </div>
        )}

        {!loading && !error && documentUrl && (
          <div className="flex justify-center h-full">
            {isPDF ? (
              // PDF Viewer using @react-pdf-viewer/core
              <div className="w-full h-full">
                <Worker workerUrl="/pdfjs/pdf.worker.min.js">
                  <Viewer
                    fileUrl={documentUrl}
                    defaultScale={SpecialZoomLevel.PageFit}
                  />
                </Worker>
              </div>
            ) : isImage ? (
              <div className="flex items-center justify-center h-full w-full">
                <div
                  className="relative overflow-hidden w-full h-full flex items-center justify-center"
                  style={{
                    touchAction: scale > 1 ? 'none' : 'auto'
                  }}
                >
                  <img
                    src={documentUrl}
                    alt={currentFileName}
                    className="max-w-full max-h-full object-contain shadow-lg rounded-lg transition-transform duration-200"
                    style={{
                      transform: `scale(${scale})`,
                      transformOrigin: 'center',
                      cursor: scale > 1 ? 'move' : 'default'
                    }}
                    draggable={false}
                  />
                  {/* Zoom Indicator on Mobile */}
                  {isMobile && scale !== 1 && (
                    <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded-lg text-xs">
                      {Math.round(scale * 100)}%
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center p-8">
                <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  Vorschau für diesen Dateityp nicht verfügbar
                </p>
                <button
                  onClick={downloadDocument}
                  className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Dokument herunterladen
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Cropper Modal */}
      {showCropper && documentUrl && (
        <ImageCropper
          imageUrl={documentUrl}
          onCrop={handleCrop}
          onCancel={() => setShowCropper(false)}
          loading={cropLoading}
        />
      )}
    </div>
  );
};

export default DocumentViewer;